import { Lecture } from '../types/Lecture';
import { School, SchoolKind, SchoolPreview } from '../types/School';
import { Subscription } from '../types/Subscription';
import { User, UserExam } from '../types/User';
import { getSchoolPictureUploadUrl } from './aws';
import axiosInstance from './axiosCreator';
import axios from 'axios';

export const fetchSchools = async (options?: {
  // Admin-only opt-in: also return ORGANIZATION webinar hosts (e.g. KAMSA).
  // The student picker never passes this.
  includeOrganizations?: boolean;
}): Promise<School[] | undefined> => {
  try {
    const response = await axiosInstance.get('/schools', {
      params: options?.includeOrganizations
        ? { includeOrganizations: 'true' }
        : undefined,
    });

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to fetch schools');
    }
    return response.data.data;
  } catch (error) {
    console.error('Failed to fetch schools:', error);
    return undefined;
  }
};

export interface UnlockCohortInfo {
  // The school's canonical cohort string; passed to register for attribution.
  cohort: string;
  schoolName: string;
  shortName: string | null;
  image: string | null;
  domains: string[];
  isUnlockActivated: boolean;
}

/**
 * Resolves the /unlock/[cohort] school-launch page (branding + eligibility
 * domains + activation flag). Returns null when no school matches the cohort
 * (the endpoint 404s) or the request fails, so the caller can redirect to
 * /auth. The caller separately gates on `isUnlockActivated`.
 */
export const fetchUnlockCohortInfo = async (
  cohort: string
): Promise<UnlockCohortInfo | null> => {
  try {
    const response = await axiosInstance.get(
      `/public/unlock/${encodeURIComponent(cohort)}`
    );

    if (!response.data?.success || !response.data?.data) {
      return null;
    }
    return response.data.data as UnlockCohortInfo;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    console.error('Failed to fetch unlock cohort info:', error);
    return null;
  }
};

// 'refused' is a definitive server answer (contract-school guard, bad cohort)
// that callers must not retry; 'error' is transport-shaped (timeout, 5xx) and
// safe to retry.
export type UnlockSchoolOutcome = 'ok' | 'refused' | 'error';

/**
 * ORA-92: associate the *currently authenticated* user with a school's unlock
 * cohort and grant that school's discount, without signing out. Called from the
 * /unlock flow when a logged-in user passes the eligibility check, and from the
 * webinar live page for already-signed-in attendees. `unlockEmail` is the edu
 * they entered, captured as a secondary email server-side.
 */
export const unlockSchoolForCurrentUserOutcome = async (params: {
  cohort: string;
  unlockEmail?: string;
}): Promise<UnlockSchoolOutcome> => {
  try {
    const response = await axiosInstance.post('/schools/unlock', params);
    return response.data?.success ? 'ok' : 'refused';
  } catch (error) {
    console.error('Failed to unlock school for current user:', error);

    const status = axios.isAxiosError(error) ? error.response?.status : null;
    return status != null && status >= 400 && status < 500
      ? 'refused'
      : 'error';
  }
};

export const unlockSchoolForCurrentUser = async (params: {
  cohort: string;
  unlockEmail?: string;
}): Promise<boolean> =>
  (await unlockSchoolForCurrentUserOutcome(params)) === 'ok';

export const fetchSchoolsForAdmin = async (): Promise<School[] | undefined> => {
  try {
    const response = await axiosInstance.get('/schools/admin');
    return response.data.data;
  } catch (error) {
    console.error('Failed to fetch schools:', error);
    return undefined;
  }
};

export interface ExamWithLectures extends UserExam {
  lectures: Lecture[];
}

export interface UserWithSubscription extends User {
  subscription: Subscription;
  flashcardResponses: number;
  vignetteResponses: number;
  isUnsubscribed: boolean;
}

export const fetchSchoolDetails = async (
  schoolId: string
): Promise<
  | {
      school: School;
      exams: ExamWithLectures[];
      students: UserWithSubscription[];
    }
  | undefined
> => {
  try {
    const response = await axiosInstance.get(`/schools/${schoolId}/details`);
    return response.data.data;
  } catch (error) {
    console.error('Failed to fetch school:', error);
    return undefined;
  }
};

export const fetchSchoolPreviews = async (
  schoolIds: string[]
): Promise<SchoolPreview[]> => {
  try {
    const response = await axiosInstance.post(`/schools/bulk/previews`, {
      schoolIds,
    });

    return response.data.data;
  } catch (error) {
    console.error('Failed to fetch school:', error);
    return [];
  }
};

export const fetchUnverifiedSchools = async (): Promise<
  School[] | undefined
> => {
  try {
    const response = await axiosInstance.get('/schools/unverified');
    return response.data;
  } catch (error) {
    console.error('Failed to fetch unverified schools:', error);
    return undefined;
  }
};

export const updateSchool = async (
  schoolId: string,
  schoolData: Partial<School>
): Promise<School | undefined> => {
  try {
    const response = await axiosInstance.put(
      `/schools/${schoolId}`,
      schoolData
    );

    return response.data;
  } catch (error) {
    console.error('Failed to update school:', error);
    return undefined;
  }
};

export const updateSchoolDomains = async (
  schoolId: string,
  domains: string[]
): Promise<School | undefined> => {
  try {
    const response = await axiosInstance.post(`/schools/${schoolId}/domains`, {
      domains,
    });

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to update school domains');
    }
    return response.data.data;
  } catch (error) {
    console.error('Failed to update school domains:', error);
    return undefined;
  }
};

export const addSchool = async (schoolName: string, userId: string) => {
  try {
    const response = await axiosInstance.post('/schools/create', {
      name: schoolName,
      userId,
    });

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to add school');
    }
    return response.data.data;
  } catch (error) {
    console.error('Failed to add school:', error);
    return undefined;
  }
};

export const updateSchoolImageBE = async (
  schoolId: string,
  image: string
): Promise<School | undefined> => {
  try {
    const response = await axiosInstance.post(`/schools/${schoolId}/image`, {
      image,
    });

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to update school image');
    }
    return response.data.data;
  } catch (error) {
    console.error('Failed to update school image:', error);
    return undefined;
  }
};

export const uploadSchoolPictureToS3 = async (file: File): Promise<string> => {
  if (!file) return '';

  const bucket = 'ora-imgs';
  const dirName = `schools/${file.name}`;
  const data = await getSchoolPictureUploadUrl(bucket, dirName, file.name);

  if (data.url) {
    try {
      await axios.put(data.url, file, {
        headers: {
          'Content-Type': file.type,
        },
      });

      return `https://ora-imgs.s3.us-west-1.amazonaws.com/${dirName}/${file.name}`;
    } catch (error) {
      console.error('Upload error:', error);
      throw new Error(
        `Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  throw new Error('Failed to get presigned URL');
};

export const fetchCohortImage = async (cohort: string) => {
  const response = await axiosInstance.get(`/public/cohort-image/${cohort}`);
  return response.data.data;
};

export const verifySchool = async (
  schoolId: string
): Promise<School | undefined> => {
  try {
    const response = await axiosInstance.post(`/schools/${schoolId}/verify`);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to verify school');
    }
    return response.data.data;
  } catch (error) {
    console.error('Failed to verify school:', error);
    return undefined;
  }
};

export const adminAddSchool = async (
  name: string,
  cohort: string,
  kind: SchoolKind = 'SCHOOL'
) => {
  const response = await axiosInstance.post('/schools/add', {
    name,
    cohort,
    kind,
  });

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to add school');
  }
  return response.data.data;
};
